import React, { memo, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { dates, formats, validators } from "investira.sdk";
import { useSelector } from "react-redux";
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  FormControlLabel,
  Typography,
  Switch,
  ListSubheader,
  ListItemText,
  Chip,
} from "investira.react.components";
import { VALIDATIONS } from "../../../const";
import { DecimalTextField } from "../../../components/molecules";
import { useRelatorioContext } from "../../../contexts/RelatorioContext";
import services from "../../../services";
import withResponseHandling from "../../../hoc/withResponseHandling";
import withSaveParams from "../../../hoc/withSaveParams";
import withMessage from "../../../hoc/withMessage";
import { INDICADOR_TIPO } from "@investirapri/financa.js/lib/enums";
import dayjs from "dayjs";

// 2) carteira
// 1) data
// 3) aberto/fechado
// 4) dias de amostragem:126
// 5) nivel de confiança: 90,92.5,95,97.5,99
// 6) juros:2.51 %
// 7) ewma peso: 94% (90 a 99)
// 8) ewma dias: 126 (sempre <= dias de de amostragem)

const VALIDATION_SCHEMA = Yup.object().shape({
  carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
  data: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
  // juros: Yup.number()
  //   .min(-10.0, "Minímo permitido é -10,0%")
  //   .max(10.0, "Máximo permitido é 10,0%")
  //   .required(VALIDATIONS.REQUIRED),
  juros: Yup.number().required(VALIDATIONS.REQUIRED),
  aberta: Yup.bool().required(VALIDATIONS.REQUIRED),
  confiancas: Yup.number()
    .min(90.0, "Minímo permitido é 90%")
    .max(99, "Máximo permitido é 99%")
    .required(VALIDATIONS.REQUIRED),
  peso_ewma: Yup.number()
    .min(90.0, "Minímo permitido é 90%")
    .max(99, "Máximo permitido é 99%")
    .required(VALIDATIONS.REQUIRED),
  dias: Yup.number().min(1).required(VALIDATIONS.REQUIRED),
  dias_ewma: Yup.number()
    .min(1)
    .max(
      Yup.ref("dias"),
      "O valor de EWMA Dias não pode ser maior que Dias de Amostragem"
    )
    .required(VALIDATIONS.REQUIRED),
});

const VarParams = memo((props) => {
  const { state, actions } = useRelatorioContext();

  const storedIndicadores = useSelector((store) => store.indicadores.data);

  const navigate = useNavigate();
  const location = useLocation();

  const storedParams = props.storedParams("var", props.lastOutput);

  const [datas, setDatas] = useState([]);
  const [posicaoAberta, setPosicaoAberta] = useState(false);
  const [benchmarks, setBenchmarks] = useState(false);

  function groupIndicadores(pValues) {
    // Reagrupar por indicador_tipo_id
    // Ordenação dos grupos: C, J, I, M
    const ordemTipoId = ["C", "J", "I", "M"];
    const xIndicadoresGroupedTemp = pValues.reduce((acc, indicador) => {
      const tipoId = indicador.indicador_tipo_id;
      if (!acc[tipoId]) {
        acc[tipoId] = [];
      }
      acc[tipoId].push(indicador);
      return acc;
    }, {});
    // Reordena os grupos conforme a ordem especificada
    const xIndicadoresGrouped = {};
    ordemTipoId.forEach((tipoId) => {
      if (xIndicadoresGroupedTemp[tipoId]) {
        xIndicadoresGrouped[tipoId] = xIndicadoresGroupedTemp[tipoId];
      }
    });

    return xIndicadoresGrouped;
  }

  const xIndicadores = useMemo(() => {
    const xIndicadoresFlatted = Object.values(storedIndicadores || {}).flatMap(
      (pLevel) => (Array.isArray(pLevel) ? pLevel : Object.values(pLevel))
    );

    const xIndicadoresFiltered = xIndicadoresFlatted.filter((xIndicador) => {
      return [
        "DI",
        "USD",
        "IPCA",
        "IGPM",
        "INPC",
        "IBOV",
        "IMAB",
        "IFIX",
      ].includes(xIndicador.indicador_id);
    });

    // Reagrupar por indicador_tipo_id
    // Ordenação dos grupos: C, J, I, M
    const xIndicadoresGrouped = groupIndicadores(xIndicadoresFiltered);

    return xIndicadoresGrouped;
  }, [storedIndicadores]);

  const xInitialValues = {
    carteira_id: "",
    data: storedParams.data || null,
    aberta: storedParams.aberta || false,
    dias: 126,
    confiancas: 95,
    juros: 1,
    peso_ewma: 94,
    dias_ewma: 126,
    consolidacao_por_emissor: "I",
    indicador_id: null,
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.risco(
        {
          ...pValues,
        },
        (rRes) => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams("var", pValues);
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  console.log("formik.values", formik.values);

  const handleNumberChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const handleSwitchChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const { ...restValues } = xValues;
      formik.setValues({ ...xInitialValues, ...restValues });
    }
    setBenchmarks(xIndicadores);
    services.benchmarks.list({}, (rRes) => {
      if (!validators.isEmpty(rRes.data)) {
        const groupedBenchmarks = groupIndicadores(rRes.data);

        setBenchmarks(groupedBenchmarks);
      }
    });
  }, []);

  useEffect(() => {
    if (state.isDialogOpen && formik.values.carteira_id !== "") {
      setDatas([]);

      formik.values.carteira_id &&
        services.posicoes.datas(
          {
            sort: "data DESC",
            carteira_id: formik.values.carteira_id,
          },
          (rRes) => {
            
            const xDates = rRes.data.map((xPosicao) => {
              return {
                value: formats.formatDateCustom(
                  dates.toDate(xPosicao.data),
                  "YYYY-MM-DD"
                ),
                label: formats.formatDateCustom(
                  dates.toDate(xPosicao.data),
                  "DD/MM/YYYY"
                ),
                aberta: xPosicao.aberta,
              };
            });

            // Verifica se há datas disponíveis antes de atualizar
            if (validators.isEmpty(xDates) || xDates.length === 0) {
              props.onMessageError("Nenhuma posição disponível para esta carteira");
              return;
            }

            setDatas(xDates);

            const xValues = storedParams;

            if (
              !validators.isEmpty(xValues) &&
              formik.values.carteira_id === xValues.carteira_id
            ) {
              const { data, aberta } = xValues;
              formik.setFieldValue("data", data);

              const xAberta = xDates.filter((x) => x.value === data)[0]?.aberta;

              setPosicaoAberta(xAberta);
              formik.setFieldValue("aberta", aberta); // Talvez não seja necessário
            } else {
              formik.setFieldValue("data", xDates[0].value);
              setPosicaoAberta(xDates[0].aberta);
            }
          },
          (rErr) => {
            props.responseErrorHandling(rErr);
          }
        );
    }
  }, [state.isDialogOpen, formik.values.carteira_id]);

  useEffect(() => {
    if (validators.isEmpty(formik.values.indicador_id)) {
      formik.setFieldValue("juros", 1);
    } else {
      const xDias = formik.values.dias * -1;
      const xDataInicio = dayjs(formik.values.data)
        .add(xDias, "day")
        .format("YYYY-MM-DD");

      services.consultas.indicadores.variacao(
        {
          indicador_id: formik.values.indicador_id,
          unidade: formik.values.dias,
          data_inicio: xDataInicio,
          data_fim: formik.values.data,
        },
        (rRes) => {
          const { taxa_ano } = rRes.data[0];
          formik.setFieldValue("juros", taxa_ano, true);
        }
      );
    }
  }, [formik.values.indicador_id, formik.values.dias, formik.values.data]);

  const xCarteiras = useMemo(
    () =>
      props.carteiras
        .slice()
        .sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras]
  );

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ width: "500px", marginBottom: 2, py: 1 }} spacing={2}>
        <Stack flexGrow={1}>
          <TextField
            id="carteira_id"
            name="carteira_id"
            label="Carteira"
            variant="outlined"
            value={formik.values.carteira_id}
            onChange={formik.handleChange}
            select
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="wallet" size={21} />
                </InputAdornment>
              ),
            }}
          >
            {xCarteiras.map((xOption) => (
              <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                {xOption.carteira}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {formik.values.carteira_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data-posicao"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={(e) => {
                      formik.handleChange(e);

                      const xAberta = datas.filter(
                        (x) => x.value === e.target.value
                      )[0].aberta;

                      setPosicaoAberta(xAberta);
                      if (!xAberta) {
                        formik.setFieldValue("aberta", false);
                      }
                    }}
                    sx={{
                      "& .MuiChip-root": {
                        display: "none",
                      },
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}
                  >
                    {datas.map((xOption, xIndex) => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={"Aberta"}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
            <Stack>
              <TextField
                id="indicador_id"
                name="indicador_id"
                label="Benchmark"
                variant="outlined"
                value={formik.values.indicador_id}
                onChange={formik.handleChange}
                select
                fullWidth
                error={Boolean(formik.errors.indicador_id)}
                helperText={formik.errors.indicador_id}
              >
                <MenuItem value={null} dense>
                  Nenhum
                </MenuItem>

                {Object.entries(benchmarks).flatMap(([xKey, xValues]) => {
                  const xHeader = (
                    <ListSubheader key={`hdr-${xKey}`} muiSkipListHighlight>
                      {INDICADOR_TIPO[xKey].descricao}
                    </ListSubheader>
                  );

                  const xItems = xValues.map((xIndicador) => (
                    <MenuItem
                      key={`itm-${xIndicador.indicador_id}`}
                      value={xIndicador.indicador_id}
                      dense
                    >
                      {`${xIndicador.indicador_id} - ${xIndicador.indicador}`}
                    </MenuItem>
                  ));
                  return [xHeader, ...xItems];
                })}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Stack sx={{ flex: "1 1" }}>
                <TextField
                  id="dias"
                  name="dias"
                  label="Dias de amostragem"
                  variant="outlined"
                  type="number"
                  value={formik.values.dias}
                  onChange={formik.handleChange}
                  fullWidth
                  error={Boolean(formik.errors.dias)}
                  helperText={formik.errors.dias}
                  inputProps={{
                    pattern: "[0-9]*",
                    formNoValidate: "formnovalidate",
                  }}
                />
              </Stack>
              <Stack sx={{ flex: "1 1" }}>
                <TextField
                  id="confiancas"
                  name="confiancas"
                  label="Nível de confiança"
                  variant="outlined"
                  value={formik.values.confiancas}
                  onChange={formik.handleChange}
                  select
                  fullWidth
                  error={Boolean(formik.errors.confiancas)}
                  helperText={formik.errors.confiancas}
                >
                  {[90, 92.5, 95, 97.5, 99].map((xOption) => (
                    <MenuItem key={crypto.randomUUID()} value={xOption}>
                      {xOption}%
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack sx={{ flex: "1 1" }}>
                <DecimalTextField
                  id="juros"
                  name="juros"
                  label="Juros Livre de Risco"
                  variant="outlined"
                  value={formik.values.juros}
                  placeholder={"1,0"}
                  onChange={(_, pValue) => handleNumberChange("juros", pValue)}
                  decimal={3}
                  allowNegative={true}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  error={Boolean(formik.errors.juros)}
                  helperText={formik.errors.juros}
                  fullWidth
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                id="dias_ewma"
                name="dias_ewma"
                label="EWMA Dias"
                variant="outlined"
                type="number"
                value={formik.values.dias_ewma}
                onChange={formik.handleChange}
                fullWidth
                error={Boolean(formik.errors.dias_ewma)}
                helperText={formik.errors.dias_ewma}
                inputProps={{
                  pattern: "[0-9]*",
                  formNoValidate: "formnovalidate",
                }}
              />

              <TextField
                id="peso_ewma"
                name="peso_ewma"
                label="Lambda EWMA"
                variant="outlined"
                type="number"
                value={formik.values.peso_ewma}
                onChange={formik.handleChange}
                fullWidth
                error={Boolean(formik.errors.peso_ewma)}
                helperText={formik.errors.peso_ewma}
                inputProps={{
                  pattern: "[0-9]*",
                  formNoValidate: "formnovalidate",
                }}
              />
            </Stack>

            <Stack px={1}>
              <FormControlLabel
                control={
                  <Switch
                    id="aberta"
                    checked={formik.values.aberta}
                    onChange={(e, value) => {
                      handleSwitchChange("aberta", value);
                    }}
                    name="aberta"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">Posição aberta</Typography>
                }
                disabled={!posicaoAberta}
              />
            </Stack>
          </>
        )}

        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={2}
        >
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={actions.handleNewConsultCancel}
          >
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              validators.isEmpty(datas) ||
              validators.isEmpty(formik.values.carteira_id)
            }
          >
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(withSaveParams(withMessage(VarParams)));
